// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsTabContainer, SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {GameOverlayPlacementPreview} from '@app/features/game_overlay/components/GameOverlayPlacementPreview';
import GameOverlaySettings from '@app/features/game_overlay/state/GameOverlaySettings';
import {IN_GAME_OVERLAY_DESCRIPTOR} from '@app/features/game_overlay/utils/GameOverlayMessageDescriptors';
import {
	clampGameOverlayScale,
	GAME_OVERLAY_SCALE_MAX,
	GAME_OVERLAY_SCALE_MAX_WITHOUT_PLACEMENT,
	GAME_OVERLAY_SCALE_MIN,
	type GameOverlayMode,
} from '@app/features/game_overlay/utils/GameOverlayModel';
import {isGameOverlayPlacementSupported} from '@app/features/game_overlay/utils/GameOverlaySupport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {Slider} from '@app/features/ui/components/Slider';
import {RadioGroup, type RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import type {DesktopGameOverlayCorner, DesktopGameOverlayDisplay} from '@app/types/electron.d';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

const GAME_OVERLAY_SCALE_STEP = 5;
const GAME_OVERLAY_SCALE_MARKER_STEP = 25;
const logger = new Logger('GameOverlaySettingsTab');

const GAME_OVERLAY_DESCRIPTION_DESCRIPTOR = msg({
	message:
		"Doesn't appear over exclusive fullscreen; switch your game to borderless windowed mode. May add a little latency in games. Appears on your primary display.",
	comment: 'Description under the in-game overlay toggle in desktop settings (Windows only).',
});
const GAME_OVERLAY_DESCRIPTION_WITH_PLACEMENT_DESCRIPTOR = msg({
	message:
		"Doesn't appear over exclusive fullscreen; switch your game to borderless windowed mode. May add a little latency in games.",
	comment:
		'Description under the in-game overlay toggle in desktop settings (Windows only), on app versions where the display can be chosen.',
});
const GAME_OVERLAY_ONLY_IN_GAME_DESCRIPTOR = msg({
	message: 'Only while a game is open',
	comment: 'Switch in the in-game overlay settings: the overlay stays hidden unless a known game is running.',
});
const GAME_OVERLAY_ONLY_IN_GAME_HINT_DESCRIPTOR = msg({
	message:
		'Hides the overlay until a game from our game list is running; it can take 15 seconds to notice. Games missing from the list will not show it. This check stays on your computer.',
	comment: 'Description under the "only while a game is open" switch in the in-game overlay settings.',
});
const GAME_OVERLAY_SCALE_DESCRIPTOR = msg({
	message: 'Size',
	comment: 'Heading above the slider that sets how large the in-game overlay is drawn.',
});
const GAME_OVERLAY_POSITION_DESCRIPTOR = msg({
	message: 'Position',
	comment: 'Heading above the small screen preview where the in-game overlay is dragged into place.',
});
const GAME_OVERLAY_POSITION_HINT_DESCRIPTOR = msg({
	message: "Drag the overlay here to place it. It can't be moved on top of a game.",
	comment:
		'Description above the small screen preview in the in-game overlay settings. The overlay itself ignores the mouse while playing.',
});
const GAME_OVERLAY_DISPLAY_DESCRIPTOR = msg({
	message: 'Screen',
	comment: 'Heading above the options choosing which monitor the in-game overlay appears on.',
});
const GAME_OVERLAY_UNNAMED_DISPLAY_DESCRIPTOR = msg({
	message: 'Screen {number}',
	comment: 'Name of a monitor that reports no name of its own. {number} is its place in the list, starting at 1.',
});
const GAME_OVERLAY_PRIMARY_DISPLAY_DESCRIPTOR = msg({
	message: 'Primary screen',
	comment: "Note next to the monitor Windows uses as its main display, in the in-game overlay's display options.",
});
const GAME_OVERLAY_MODE_DESCRIPTOR = msg({
	message: 'Who to show',
	comment: 'Heading above the in-game overlay mode options (only people speaking, or everyone in the voice channel).',
});
const GAME_OVERLAY_CORNER_DESCRIPTOR = msg({
	message: 'Corner',
	comment: 'Heading above the options choosing which screen corner the in-game overlay appears in.',
});
const GAME_OVERLAY_MODE_SPEAKING_DESCRIPTOR = msg({
	message: 'Speaking',
	comment: 'In-game overlay mode option: show only people who are currently speaking.',
});
const GAME_OVERLAY_MODE_ROOM_DESCRIPTOR = msg({
	message: 'Everyone in the channel',
	comment: 'In-game overlay mode option: show everyone in the voice channel, speaking or not.',
});
const GAME_OVERLAY_CORNER_TOP_LEFT_DESCRIPTOR = msg({
	message: 'Top left',
	comment: 'In-game overlay position option: top-left corner of the screen.',
});
const GAME_OVERLAY_CORNER_TOP_RIGHT_DESCRIPTOR = msg({
	message: 'Top right',
	comment: 'In-game overlay position option: top-right corner of the screen.',
});
const GAME_OVERLAY_CORNER_BOTTOM_LEFT_DESCRIPTOR = msg({
	message: 'Bottom left',
	comment: 'In-game overlay position option: bottom-left corner of the screen.',
});
const GAME_OVERLAY_CORNER_BOTTOM_RIGHT_DESCRIPTOR = msg({
	message: 'Bottom right',
	comment: 'In-game overlay position option: bottom-right corner of the screen.',
});

function useGameOverlayDisplays(): Array<DesktopGameOverlayDisplay> {
	const [displays, setDisplays] = useState<Array<DesktopGameOverlayDisplay>>([]);
	useEffect(() => {
		let active = true;
		void getElectronAPI()
			?.getGameOverlayDisplays?.()
			.then((next) => {
				if (active) setDisplays(next);
			})
			.catch((error) => logger.warn('Failed to list displays for the game overlay', error));
		return () => {
			active = false;
		};
	}, []);
	return displays;
}

const GameOverlaySettingsTab: React.FC = observer(() => {
	const {i18n} = useLingui();
	const placementSupported = isGameOverlayPlacementSupported();
	const displays = useGameOverlayDisplays();
	const maxScale = placementSupported ? GAME_OVERLAY_SCALE_MAX : GAME_OVERLAY_SCALE_MAX_WITHOUT_PLACEMENT;
	const scale = clampGameOverlayScale(GameOverlaySettings.scale, maxScale);
	const scaleMarkers = Array.from(
		{length: (maxScale - GAME_OVERLAY_SCALE_MIN) / GAME_OVERLAY_SCALE_MARKER_STEP + 1},
		(_, index) => GAME_OVERLAY_SCALE_MIN + index * GAME_OVERLAY_SCALE_MARKER_STEP,
	);
	const selectedDisplay =
		displays.find((display) => display.id === GameOverlaySettings.displayId) ??
		displays.find((display) => display.primary) ??
		null;
	const modeOptions: ReadonlyArray<RadioOption<GameOverlayMode>> = [
		{value: 'speaking', name: i18n._(GAME_OVERLAY_MODE_SPEAKING_DESCRIPTOR)},
		{value: 'room', name: i18n._(GAME_OVERLAY_MODE_ROOM_DESCRIPTOR)},
	];
	const cornerOptions: ReadonlyArray<RadioOption<DesktopGameOverlayCorner>> = [
		{value: 'top-left', name: i18n._(GAME_OVERLAY_CORNER_TOP_LEFT_DESCRIPTOR)},
		{value: 'top-right', name: i18n._(GAME_OVERLAY_CORNER_TOP_RIGHT_DESCRIPTOR)},
		{value: 'bottom-left', name: i18n._(GAME_OVERLAY_CORNER_BOTTOM_LEFT_DESCRIPTOR)},
		{value: 'bottom-right', name: i18n._(GAME_OVERLAY_CORNER_BOTTOM_RIGHT_DESCRIPTOR)},
	];
	const displayOptions: ReadonlyArray<RadioOption<string>> = displays.map((display, index) => ({
		value: display.id,
		name: display.label || i18n._(GAME_OVERLAY_UNNAMED_DISPLAY_DESCRIPTOR, {number: index + 1}),
		desc: [
			`${display.width} × ${display.height}`,
			...(display.primary ? [i18n._(GAME_OVERLAY_PRIMARY_DISPLAY_DESCRIPTOR)] : []),
		].join(' · '),
	}));
	return (
		<SettingsTabContainer data-flx="game-overlay.game-overlay-settings-tab.settings-tab-container">
			<Switch
				label={i18n._(IN_GAME_OVERLAY_DESCRIPTOR)}
				description={i18n._(
					placementSupported ? GAME_OVERLAY_DESCRIPTION_WITH_PLACEMENT_DESCRIPTOR : GAME_OVERLAY_DESCRIPTION_DESCRIPTOR,
				)}
				value={GameOverlaySettings.enabled}
				onChange={GameOverlaySettings.setEnabled}
				data-flx="game-overlay.game-overlay-settings-tab.switch.enabled"
			/>
			{placementSupported && (
				<Switch
					label={i18n._(GAME_OVERLAY_ONLY_IN_GAME_DESCRIPTOR)}
					description={i18n._(GAME_OVERLAY_ONLY_IN_GAME_HINT_DESCRIPTOR)}
					value={GameOverlaySettings.onlyInGame}
					disabled={!GameOverlaySettings.enabled}
					onChange={GameOverlaySettings.setOnlyInGame}
					data-flx="game-overlay.game-overlay-settings-tab.switch.only-in-game"
				/>
			)}
			<SettingsTabSection
				title={i18n._(GAME_OVERLAY_MODE_DESCRIPTOR)}
				data-flx="game-overlay.game-overlay-settings-tab.mode-section"
			>
				<RadioGroup
					options={modeOptions}
					value={GameOverlaySettings.mode}
					disabled={!GameOverlaySettings.enabled}
					onChange={GameOverlaySettings.setMode}
					aria-label={i18n._(GAME_OVERLAY_MODE_DESCRIPTOR)}
					data-flx="game-overlay.game-overlay-settings-tab.radio-group.mode"
				/>
			</SettingsTabSection>
			<SettingsTabSection
				title={i18n._(GAME_OVERLAY_SCALE_DESCRIPTOR)}
				data-flx="game-overlay.game-overlay-settings-tab.scale-section"
			>
				<Slider
					value={scale}
					defaultValue={scale}
					factoryDefaultValue={100}
					minValue={GAME_OVERLAY_SCALE_MIN}
					maxValue={maxScale}
					step={GAME_OVERLAY_SCALE_STEP}
					markers={scaleMarkers}
					stickToMarkers={false}
					disabled={!GameOverlaySettings.enabled}
					ariaLabel={i18n._(GAME_OVERLAY_SCALE_DESCRIPTOR)}
					onMarkerRender={(value) => `${value}%`}
					onValueRender={(value) => `${Math.round(value)}%`}
					asValueChanges={GameOverlaySettings.setScale}
					onValueChange={GameOverlaySettings.setScale}
					data-flx="game-overlay.game-overlay-settings-tab.slider.scale"
				/>
			</SettingsTabSection>
			{placementSupported && selectedDisplay && (
				<SettingsTabSection
					title={i18n._(GAME_OVERLAY_POSITION_DESCRIPTOR)}
					description={i18n._(GAME_OVERLAY_POSITION_HINT_DESCRIPTOR)}
					data-flx="game-overlay.game-overlay-settings-tab.position-section"
				>
					<GameOverlayPlacementPreview
						display={selectedDisplay}
						scale={scale}
						disabled={!GameOverlaySettings.enabled}
						data-flx="game-overlay.game-overlay-settings-tab.placement-preview"
					/>
				</SettingsTabSection>
			)}
			<SettingsTabSection
				title={i18n._(GAME_OVERLAY_CORNER_DESCRIPTOR)}
				data-flx="game-overlay.game-overlay-settings-tab.corner-section"
			>
				<RadioGroup
					options={cornerOptions}
					value={GameOverlaySettings.corner}
					disabled={!GameOverlaySettings.enabled}
					onChange={GameOverlaySettings.setCorner}
					aria-label={i18n._(GAME_OVERLAY_CORNER_DESCRIPTOR)}
					data-flx="game-overlay.game-overlay-settings-tab.radio-group.corner"
				/>
			</SettingsTabSection>
			{placementSupported && displayOptions.length > 1 && selectedDisplay && (
				<SettingsTabSection
					title={i18n._(GAME_OVERLAY_DISPLAY_DESCRIPTOR)}
					data-flx="game-overlay.game-overlay-settings-tab.display-section"
				>
					<RadioGroup
						options={displayOptions}
						value={selectedDisplay.id}
						disabled={!GameOverlaySettings.enabled}
						onChange={GameOverlaySettings.setDisplayId}
						aria-label={i18n._(GAME_OVERLAY_DISPLAY_DESCRIPTOR)}
						data-flx="game-overlay.game-overlay-settings-tab.radio-group.display"
					/>
				</SettingsTabSection>
			)}
		</SettingsTabContainer>
	);
});

export default GameOverlaySettingsTab;
